import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import React from 'react';
import { createPortal } from 'react-dom';

import type { SendOutreachResult } from './outreachImportService';

export type OutreachSendModalState =
  | { open: false }
  | { open: true; phase: 'sending'; recipientCount: number }
  | { open: true; phase: 'success'; recipientCount: number; result: SendOutreachResult }
  | { open: true; phase: 'error'; message: string };

export const CLOSED_SEND_MODAL: OutreachSendModalState = { open: false };

interface OutreachSendEmailModalProps {
  state: OutreachSendModalState;
  onClose: () => void;
}

function recipientLabel(count: number): string {
  return count === 1 ? '1 contact' : `${count} contacts`;
}

export function OutreachSendEmailModal({ state, onClose }: OutreachSendEmailModalProps) {
  if (!state.open) return null;

  const isSending = state.phase === 'sending';
  const isSuccess = state.phase === 'success';
  const isError = state.phase === 'error';

  return createPortal(
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[100] bg-black/35 backdrop-blur-[2px]"
        onClick={isSending ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="outreach-send-modal-title"
        aria-busy={isSending}
        className="fixed left-1/2 top-1/2 z-[101] w-[min(92vw,400px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl shadow-2xl overflow-hidden"
        style={{
          background: 'var(--surface-card-elevated, var(--vscode-bg-elevated, #fff))',
          border: '1px solid var(--vscode-border-secondary, #e5e7eb)',
        }}
      >
        <div className="px-6 py-6 text-center">
          {isSending && (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
                   style={{ background: 'rgba(108,71,255,0.1)' }}>
                <Loader2 className="h-7 w-7 animate-spin text-purple-600" aria-hidden />
              </div>
              <h2 id="outreach-send-modal-title" className="text-[17px] font-semibold mb-1"
                  style={{ color: 'var(--vscode-text-primary)' }}>
                Sending email…
              </h2>
              <p className="text-[13px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                Please wait while we send to {recipientLabel(state.recipientCount)}.
              </p>
            </>
          )}

          {isSuccess && (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
                   style={{ background: 'rgba(21,128,61,0.12)' }}>
                <CheckCircle2 className="h-7 w-7 text-green-600" aria-hidden />
              </div>
              <h2 id="outreach-send-modal-title" className="text-[17px] font-semibold mb-1"
                  style={{ color: 'var(--vscode-text-primary)' }}>
                Email sent to {recipientLabel(state.result.sent ?? state.recipientCount)}
              </h2>
              <p className="text-[13px] mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                Your outreach emails were delivered via Resend.
              </p>
              {(state.result.skipped ?? 0) > 0 || (state.result.failed ?? 0) > 0 ? (
                <p className="text-[12px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                  {(state.result.skipped ?? 0) > 0 && `${state.result.skipped} skipped`}
                  {(state.result.skipped ?? 0) > 0 && (state.result.failed ?? 0) > 0 && ' · '}
                  {(state.result.failed ?? 0) > 0 && `${state.result.failed} failed`}
                </p>
              ) : null}
            </>
          )}

          {isError && (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
                   style={{ background: 'rgba(239,68,68,0.1)' }}>
                <Mail className="h-7 w-7 text-red-500" aria-hidden />
              </div>
              <h2 id="outreach-send-modal-title" className="text-[17px] font-semibold mb-1"
                  style={{ color: 'var(--vscode-text-primary)' }}>
                Could not send email
              </h2>
              <p className="text-[13px]" style={{ color: '#b91c1c' }}>
                {state.message}
              </p>
            </>
          )}
        </div>

        {!isSending && (
          <div className="px-6 pb-6">
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 rounded-xl text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: 'var(--brand-purple, #6C47FF)' }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}

export async function runOutreachSendWithModal(
  recipientCount: number,
  setModal: React.Dispatch<React.SetStateAction<OutreachSendModalState>>,
  sendFn: () => Promise<SendOutreachResult>,
  opts?: { confirm?: string },
): Promise<SendOutreachResult | null> {
  if (opts?.confirm && !window.confirm(opts.confirm)) return null;

  setModal({ open: true, phase: 'sending', recipientCount });
  const result = await sendFn();

  if (!result.ok) {
    setModal({ open: true, phase: 'error', message: result.error ?? 'Send failed' });
    return result;
  }

  setModal({
    open: true,
    phase: 'success',
    recipientCount,
    result,
  });
  return result;
}
